import { CSSProperties } from 'react';
import { Controller, Control, FieldValues } from 'react-hook-form';
import { FormControl, Select, MenuItem, Stack, SelectProps } from '@mui/material';

import TextFormLabel from '../text/TextFormLabel';
import TextFormError from '../text/TextFormError';
import { useLocalization } from '../../../localization';
import CaretDownIcon from '../icons/CaretDownIcon';

export interface SelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

type Props<T extends FieldValues = FieldValues> = Omit<SelectProps, 'onChange' | 'value' | 'defaultValue'> & {
  name: string;
  control: Control<T>;
  options: SelectOption[];
  label: string;
  required?: boolean;
  defaultValue?: string | number;
  disabled?: boolean;
  loading?: boolean;
  style?: CSSProperties;
  selectStyle?: CSSProperties;
  menuHeight?: number;

  /** Callback when the value changes */
  onChange?: (value: string | number) => void;
};

export const InputSelectField = <T extends FieldValues = FieldValues>(props: Props<T>) => {
  const {
    name,
    control,
    options,
    label,
    required = false,
    defaultValue,
    disabled = false,
    loading = false,
    onChange,
    style,
    selectStyle,
    menuHeight = 500,
    ...selectProps
  } = props;
  const { t } = useLocalization();

  return (
    <Stack spacing={0.5}>
      {label && <TextFormLabel label={t(label)} required={required} />}
      <Controller
        name={name as any}
        control={control}
        defaultValue={defaultValue as any}
        render={({ field: { value, onChange: fieldOnChange, onBlur }, fieldState: { error } }) => {
          return (
            <FormControl
              variant="outlined"
              fullWidth
              error={Boolean(error)}
              disabled={disabled || loading}
              style={style}
            >
              <Select
                style={selectStyle}
                value={value}
                onBlur={onBlur}
                onChange={event => {
                  fieldOnChange(event.target.value);
                  onChange?.(event.target.value as string | number);
                }}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: menuHeight,
                    },
                  },
                }}
                IconComponent={props => <CaretDownIcon {...props} style={{ marginRight: '0.5rem' }} />}
                defaultValue={defaultValue}
                {...selectProps}
                size="small"
              >
                {options.map(opt => (
                  <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                    {t(opt.label)}
                  </MenuItem>
                ))}
              </Select>
              <TextFormError error={error} />
            </FormControl>
          );
        }}
      />
    </Stack>
  );
};
